import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import BugReportIcon from "@mui/icons-material/BugReport";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { useThemeMode } from "@context/ThemeContext";
import { useLanguageToggle } from "@hooks/useLanguageToggle";
import { DRAWER_WIDTH, NAV_ITEMS, NavSection } from "./constants";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const { switchTargetLabel, toggle: toggleLang } = useLanguageToggle();

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiresAuth || isAuthenticated,
  );

  const mainItems = visibleItems.filter(
    (item) => item.section === NavSection.Main,
  );
  const managementItems = visibleItems.filter(
    (item) => item.section === NavSection.Management,
  );

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    onMobileClose();
  };

  const renderNavItem = (item: (typeof NAV_ITEMS)[0]) => (
    <ListItemButton
      key={item.path}
      selected={location.pathname === item.path}
      onClick={() => handleNav(item.path)}
      sx={{ mb: 0.5 }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <item.icon
          sx={{
            color:
              location.pathname === item.path
                ? "primary.main"
                : "text.secondary",
          }}
        />
      </ListItemIcon>
      <ListItemText
        primary={t(item.i18nKey)}
        slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
      />
    </ListItemButton>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          cursor: "pointer",
        }}
        onClick={() => handleNav("/")}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: 2,
          }}
        >
          <SportsSoccerIcon sx={{ color: "white", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            {t("app.name")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontSize: 10,
            }}
          >
            {t("app.subtitle")}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2, px: 1.5, overflow: "auto" }}>
        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            px: 1.5,
            mb: 0.5,
            display: "block",
            fontSize: 11,
          }}
        >
          {t("nav.sections.main")}
        </Typography>
        <List disablePadding>{mainItems.map(renderNavItem)}</List>

        {managementItems.length > 0 && (
          <>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                px: 1.5,
                mt: 2,
                mb: 0.5,
                display: "block",
                fontSize: 11,
              }}
            >
              {t("nav.sections.management")}
            </Typography>
            <List disablePadding>{managementItems.map(renderNavItem)}</List>
          </>
        )}
        <List disablePadding sx={{ mt: 1 }}>
          <ListItemButton
            component="a"
            href="https://bugs.pinehood.tech/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <BugReportIcon sx={{ color: "text.secondary" }} />
            </ListItemIcon>
            <ListItemText
              primary={t("nav.reportBug")}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
            />
          </ListItemButton>
        </List>
      </Box>

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={toggleTheme}
          size="small"
          title={t(mode === "dark" ? "common.lightMode" : "common.darkMode")}
        >
          {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        <Button
          size="small"
          onClick={toggleLang}
          sx={{ minWidth: 40, fontWeight: 600 }}
        >
          {switchTargetLabel}
        </Button>
      </Box>

      {isAuthenticated && (
        <>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <ListItemButton onClick={handleLogout} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={t("nav.signOut")}
                slotProps={{
                  primary: { sx: { fontSize: 14, fontWeight: 500 } },
                }}
              />
            </ListItemButton>
          </Box>
        </>
      )}
    </Box>
  );

  if (isDesktop) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        flexShrink: 0,
        width: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          maxWidth: "min(100vw - 32px, 288px)",
          boxSizing: "border-box",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
